import { useState } from 'react';

const TONES = {
  slate: 'bg-slate-100 text-slate-700',
  blue: 'bg-blue-50 text-blue-700',
  violet: 'bg-violet-50 text-violet-700',
  pink: 'bg-pink-50 text-pink-700',
  green: 'bg-green-50 text-green-700',
  amber: 'bg-amber-50 text-amber-700',
  red: 'bg-red-50 text-red-700',
};

export const formatGBP = (value) =>
  `£${Number(value || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export const timeAgo = (iso) => {
  if (!iso) return '';
  const date = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.floor((Date.now() - date.getTime()) / 86400000);
  if (days < 0) return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 14) return 'last week';
  if (days < 60) return `${Math.floor(days / 7)} weeks ago`;
  return `${Math.floor(days / 30)} months ago`;
};

export const StatStrip = ({ items }) => (
  <div className="grid grid-cols-2 lg:grid-flow-col lg:auto-cols-fr rounded-2xl border border-slate-200 bg-white overflow-hidden">
    {items.map((item, i) => {
      const Icon = item.icon;
      return (
        <div key={item.label} className={`p-4 ${i > 0 ? 'border-l border-slate-100' : ''} ${i >= 2 ? 'border-t lg:border-t-0 border-slate-100' : ''}`}>
          <p className="flex items-center gap-1.5 text-xs text-slate-500">
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {item.label}
          </p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{item.value}</p>
          {item.sub && <p className="text-xs text-slate-400 mt-0.5">{item.sub}</p>}
        </div>
      );
    })}
  </div>
);

const KanbanBoard = ({ columns, onMove }) => {
  const [dragOver, setDragOver] = useState(null);
  const dropProps = (col) => (onMove ? {
    onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOver !== col.key) setDragOver(col.key); },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); },
    onDrop: (e) => {
      e.preventDefault();
      setDragOver(null);
      try {
        const { cardId, from } = JSON.parse(e.dataTransfer.getData('application/json'));
        if (from === col.key) return;
        const source = columns.find(c => c.key === from);
        const card = source?.cards.find(c => (c.id || c.lead_id) === cardId);
        if (card) onMove(card, from, col.key);
      } catch { /* not one of our cards */ }
    },
  } : {});

  return (
  <div className="overflow-x-auto pb-2 -mx-1 px-1">
    <div className="flex gap-3 min-w-max">
      {columns.map((col) => (
        <div key={col.key} {...dropProps(col)}
          className={`w-72 flex-shrink-0 rounded-2xl border bg-slate-50/60 flex flex-col max-h-[70vh] transition-colors ${dragOver === col.key ? 'border-pink-400 ring-2 ring-pink-200 bg-pink-50/40' : 'border-slate-200'}`}>
          <div className={`flex items-center justify-between gap-2 px-4 py-3 rounded-t-2xl ${TONES[col.tone] || TONES.slate}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{col.title}</p>
              {col.subtitle && <p className="text-xs opacity-80 truncate">{col.subtitle}</p>}
            </div>
            <span className="text-xs font-semibold bg-white/80 rounded-full px-2 py-0.5">{col.count ?? col.cards.length}</span>
          </div>
          <div className="p-2 space-y-2 overflow-y-auto flex-1">
            {col.cards.length === 0 ? (
              <p className="text-center text-xs text-slate-400 py-10">{col.emptyText || 'Nothing here'}</p>
            ) : (
              col.cards.map((card) => (
                <div
                  key={card.id || card.lead_id}
                  draggable={!!onMove}
                  onDragStart={onMove ? (e) => {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('application/json', JSON.stringify({ cardId: card.id || card.lead_id, from: col.key }));
                  } : undefined}
                  className={`rounded-xl bg-white border border-slate-200 p-3 shadow-sm ${onMove ? 'cursor-grab active:cursor-grabbing' : ''}`}
                >
                  {col.renderCard(card)}
                </div>
              ))
            )}
            {col.count > col.cards.length && (
              <p className="text-center text-xs text-slate-400 py-2">Showing {col.cards.length} of {col.count}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

export const CardTag = ({ children, tone = 'slate' }) => (
  <span className={`inline-block text-[11px] font-medium rounded-md px-1.5 py-0.5 ${TONES[tone] || TONES.slate}`}>{children}</span>
);

export default KanbanBoard;
